import React, { useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Snackbar
} from '@mui/material';
import {
  Add,
  Delete,
  LocationOn,
  Phone,
  InfoOutlined,
  History,
  Close,
  ContentCopy
} from '@mui/icons-material';
import moment from 'moment';
import Title from '../../components/Title';
import AddLostScooterModal from '../../modals/AddLostScooterModal';
import {
  getLostScootersAsync,
  removeLostScooterAsync,
  selectLostScooters,
  QRScan
} from '../../redux/slices/lostScootersSlice';
import { TableCell as MuiTableCell } from '@mui/material';

const Root = styled('div')(({ theme }) => ({
  padding: '0',
  minHeight: 'calc(100vh - 180px)'
}));

const HeaderBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 20,
  gap: 12
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(80, 115, 248, 0.08)' : 'rgba(80, 115, 248, 0.12)',
  },
  transition: 'background-color 0.2s ease'
}));

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
  padding: '12px 16px'
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
  borderBottom: `2px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
  padding: '12px 16px'
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: '#5073F8',
  color: '#FFFFFF !important',
  '& .MuiSvgIcon-root': {
    color: '#FFFFFF !important'
  },
  '&:hover': {
    backgroundColor: '#3D5FD9',
    color: '#FFFFFF !important',
    '& .MuiSvgIcon-root': {
      color: '#FFFFFF !important'
    }
  }
}));

const HistoryTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
  }
}));

interface GroupedScooter {
  scooterId: string;
  scooterName: string;
  scansCount: number;
  latestScan?: QRScan & { scooterId?: string };
  allScans: Array<QRScan & { scooterId?: string }>;
}

const LostScootersPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { items, loading, qrScans } = useSelector(selectLostScooters);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedScooter, setSelectedScooter] = useState<GroupedScooter | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    dispatch(getLostScootersAsync());
  }, [dispatch]);

  const handleAddScooter = () => {
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

  const handleRemoveScooter = (event: React.MouseEvent, scooterId: string, scooterName: string) => {
    event.stopPropagation();
    if (window.confirm(t('lostScooters.confirmRemove', { name: scooterName }))) {
      dispatch(removeLostScooterAsync(scooterId, () => {
        dispatch(getLostScootersAsync());
      }));
    }
  };

  const handleRowClick = (grouped: GroupedScooter) => {
    if (grouped.scansCount > 0) {
      setSelectedScooter(grouped);
      setHistoryModalOpen(true);
    }
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setSelectedScooter(null);
  };

  const formatCoordinates = (lat?: number, lon?: number) => {
    if (lat === undefined || lat === null || lon === undefined || lon === null) return null;
    return { lat, lon };
  };

  const handleCopyCoordinates = (event: React.MouseEvent, lat?: number, lon?: number) => {
    event.stopPropagation();
    if (lat !== undefined && lat !== null && lon !== undefined && lon !== null) {
      const coordinates = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
      navigator.clipboard.writeText(coordinates).then(() => {
        setSnackbarMessage(t('lostScooters.coordinatesCopied'));
        setSnackbarOpen(true);
      }).catch(() => {
        setSnackbarMessage(t('lostScooters.copyError'));
        setSnackbarOpen(true);
      });
    } else {
      setSnackbarMessage(t('lostScooters.coordinatesMissing'));
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Group scans by scooter
  const groupedScooters: GroupedScooter[] = items.map((scooter) => {
    const scooterScans = qrScans.filter((scan: any) => scan.scooterId === scooter._id);
    
    // Sort scans by date (newest first)
    const sortedScans = [...scooterScans].sort((a: any, b: any) => 
      moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
    );

    return {
      scooterId: scooter._id,
      scooterName: scooter.name,
      scansCount: sortedScans.length,
      latestScan: sortedScans[0] || undefined,
      allScans: sortedScans
    };
  });

  return (
    <Root>
      <Title>{t('lostScooters.title')}</Title>

      <Alert 
        severity="info" 
        icon={<InfoOutlined />}
        sx={{ 
          marginTop: 2,
          marginBottom: 2,
          borderRadius: '8px',
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          {t('lostScooters.importantLabel')}
        </Typography>
        <Typography variant="body2">
          {t('lostScooters.infoStatusPrefix')} <strong>{t('lostScooters.infoStatusActive')}</strong>{t('lostScooters.infoStatusSuffix')}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>{t('lostScooters.tipLabel')}</strong> {t('lostScooters.tipText')}
        </Typography>
      </Alert>

      <HeaderBar>
        <Box />
        <ActionButton
          startIcon={<Add />}
          onClick={handleAddScooter}
        >
          {t('lostScooters.addBtn')}
        </ActionButton>
      </HeaderBar>

      <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : groupedScooters.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1" color="textSecondary">
              {t('lostScooters.emptyState')}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('lostScooters.colName')}</TableHeaderCell>
                <TableHeaderCell>{t('lostScooters.colLastScan')}</TableHeaderCell>
                <TableHeaderCell>{t('lostScooters.colPhone')}</TableHeaderCell>
                <TableHeaderCell>{t('lostScooters.colCoordinates')}</TableHeaderCell>
                <TableHeaderCell>{t('lostScooters.colUser')}</TableHeaderCell>
                <TableHeaderCell align="center">{t('lostScooters.colScansCount')}</TableHeaderCell>
                <TableHeaderCell align="center">{t('lostScooters.colActions')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedScooters.map((grouped) => {
                const scan = grouped.latestScan;
                return (
                  <StyledTableRow 
                    key={grouped.scooterId}
                    onClick={() => handleRowClick(grouped)}
                  >
                    <StyledTableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {grouped.scansCount > 1 && (
                          <Tooltip title={t('lostScooters.multipleScansTooltip')}>
                            <History sx={{ fontSize: 18, color: '#5073F8' }} />
                          </Tooltip>
                        )}
                        {grouped.scooterName}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      {scan ? moment(scan.createdAt).format('DD.MM.YYYY HH:mm:ss') : '-'}
                    </StyledTableCell>
                    <StyledTableCell>
                      {scan?.user?.phone ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Phone sx={{ fontSize: 18, color: '#5073F8' }} />
                          {scan.user.phone}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {scan?.lat !== undefined && scan?.lat !== null && scan?.lon !== undefined && scan?.lon !== null ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationOn sx={{ fontSize: 18, color: '#4CAF50' }} />
                          {`${scan.lat.toFixed(6)}, ${scan.lon.toFixed(6)}`}
                          <Tooltip title={t('lostScooters.copyCoordinatesTooltip')}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleCopyCoordinates(e, scan.lat, scan.lon)}
                              sx={{ 
                                padding: '4px',
                                '&:hover': { 
                                  color: '#5073F8',
                                  backgroundColor: 'rgba(80, 115, 248, 0.08)'
                                }
                              }}
                            >
                              <ContentCopy sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {scan?.user ? `${scan.user.firstName || ''} ${scan.user.lastName || ''}`.trim() : '-'}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Chip 
                        label={grouped.scansCount}
                        size="small"
                        color={grouped.scansCount > 0 ? 'primary' : 'default'}
                        sx={{ 
                          fontWeight: 600,
                          minWidth: 40
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={t('lostScooters.removeTooltip')}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleRemoveScooter(e, grouped.scooterId, grouped.scooterName)}
                          sx={{ color: '#f44336' }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <AddLostScooterModal
        open={addModalOpen}
        onClose={handleCloseAddModal}
      />

      {/* Scans History Modal */}
      <Dialog
        open={historyModalOpen}
        onClose={handleCloseHistoryModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t('lostScooters.scansHistoryTitle', { name: selectedScooter?.scooterName })}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              {t('lostScooters.totalScans', { count: selectedScooter?.scansCount })}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseHistoryModal} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t('lostScooters.colNumber')}</TableHeaderCell>
                  <TableHeaderCell>{t('lostScooters.colDateTime')}</TableHeaderCell>
                  <TableHeaderCell>{t('lostScooters.colPhone')}</TableHeaderCell>
                  <TableHeaderCell>{t('lostScooters.colCoordinates')}</TableHeaderCell>
                  <TableHeaderCell>{t('lostScooters.colUser')}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedScooter?.allScans.map((scan, index) => (
                  <HistoryTableRow key={`${selectedScooter.scooterId}-${index}`}>
                    <StyledTableCell>{index + 1}</StyledTableCell>
                    <StyledTableCell>
                      {moment(scan.createdAt).format('DD.MM.YYYY HH:mm:ss')}
                    </StyledTableCell>
                    <StyledTableCell>
                      {scan.user?.phone ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Phone sx={{ fontSize: 16, color: '#5073F8' }} />
                          {scan.user.phone}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {scan.lat !== undefined && scan.lat !== null && scan.lon !== undefined && scan.lon !== null ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationOn sx={{ fontSize: 16, color: '#4CAF50' }} />
                          {`${scan.lat.toFixed(6)}, ${scan.lon.toFixed(6)}`}
                          <Tooltip title={t('lostScooters.copyCoordinatesTooltip')}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleCopyCoordinates(e, scan.lat, scan.lon)}
                              sx={{ 
                                padding: '4px',
                                '&:hover': { 
                                  color: '#5073F8',
                                  backgroundColor: 'rgba(80, 115, 248, 0.08)'
                                }
                              }}
                            >
                              <ContentCopy sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {scan.user ? `${scan.user.firstName || ''} ${scan.user.lastName || ''}`.trim() : '-'}
                    </StyledTableCell>
                  </HistoryTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseHistoryModal}
            variant="contained"
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            {t('lostScooters.closeBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor: theme.palette.mode === 'light' ? '#323232' : '#F5F5F5',
            color: theme.palette.mode === 'light' ? '#FFFFFF' : '#000000',
            borderRadius: '8px',
            fontWeight: 500
          }
        }}
      />
    </Root>
  );
};

export default LostScootersPage;
